/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  HttpRequest,
  HttpErrorResponse,
  HttpHandlerFn,
  HttpResponse,
  HttpEvent,
  HttpStatusCode,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { inject } from '@angular/core';
import { Store } from '@app/config/store';
import { configProvider } from '@app/config/app';
// import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AppConfig, BaseApiResp } from '@app/types/global.type';
import { mgmtApis, publicApis } from '@app/constant/app.const';

const whiteListedFileDownloadURLs = [
  `invoice/download/gstin/pdf/returnsummary`,
  `invoice/downloadReturnSummary`,
  `invoice/downloadGstinAndIrisReturnSummary`,
  `localhost.truecopy.in`,
  'invoice/downloadCSV',
  'gstr6/reconcilation/downloadGstr6ReconciledData',
  'gstr6a/download',
  'subscription/active',
  'invoice/count/error/warning',
  'bulk/invoice/count/error/warning',
  'gstn/proceed/file',
  'gstn/communication',
  'vendor/gstinMailIds',
  'bulk/invoice/delete',
  'bulk/gstr1/getTaxpayerType',
  'gstn/status',
  'evc/sendOtp',
  'gstn/gstr6/saveITCDetails',
  'gstr6/view/gstnITCData',
  'gstr6/view/irisITCData',
  'reconcilation/ctinDetails',
  'gstn/bulk/communication',
  'gstn/negliability/ledger/details',
  'bulk/invoice/create',
  'bulk/invoice/discard',
  'bulk/invoice/communication',
  'bulk/invoice/generate/summary',
  'bulk/invoice/download/gstn/summary',
];

const roundLoaderURLs = ['bulk/create/view'];

export function HttpInterceptor(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
): Observable<HttpEvent<any>> {
  const config = inject(configProvider);
  const store = inject(Store);
  // const router = inject(Router);
  const toastr = inject(ToastrService);

  const modifiedRequest = setApiConfig(request, config, store);
  if (!roundLoaderURLs.includes(request.url)) {
    store.appLoader.set(true);
  }

  return next(modifiedRequest).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        store.appLoader.set(false);

        const body = event?.body as BaseApiResp;
        for (const url of whiteListedFileDownloadURLs) {
          if (event.url?.includes(url)) {
            return event.clone({ body: { ...body } });
          }
        }
        if (
          body?.status?.toUpperCase() === 'FAILURE' &&
          body?.message.toLowerCase() ===
            'no such id found in database. please pass proper status id.'
        ) {
          throw new HttpErrorResponse({ error: body?.message });
        }

        const status = ['SUCCESS', 'INPROGRESS', 'ACCEPTED'].includes(
          (body?.status ?? '')?.toUpperCase(),
        );

        if (!status && body?.status != undefined) {
          let message =
            "We couldn't process your request at the moment. Please try again later.";
          if (
            body &&
            body.response &&
            body.response.error &&
            body.response.error.message
          ) {
            message =
              body.response.error.message ??
              "We couldn't process your request at the moment. Please try again later.";
            if (
              !modifiedRequest.url.includes(`gstn/data`) &&
              !modifiedRequest.url.includes(`company/businessSettings`)
            ) {
              toastr.error(message, 'Error');
              return event.clone({ body: { ...body, status } });
            }
          }
          if (body?.message.toLowerCase() === 'no data found') {
            return event.clone({ body: { ...body, status } });
          }
          if (
            body?.message.toLowerCase() === 'unauthorized user' &&
            modifiedRequest.url?.includes('company/businessSettings')
          ) {
            return event.clone({ body: { ...body, status } });
          }
          // Unauthorized user
          if (body?.message) {
            message = body?.message ?? message;
          } else {
            message = body?.response['MESSAGE'] ?? message;
          }
          if (!modifiedRequest.url.includes(`gstn/data`)) {
            toastr.error(message, 'Error');
          }
        }

        return event.clone({ body: { ...body, status } });
      }

      return event;
    }),
    catchError((resp: HttpErrorResponse) => {
      store.appLoader.set(false);
      store.roundLoader.set(false);
      if (
        resp.status === HttpStatusCode.Forbidden &&
        !resp.url?.includes(`user/company/assignedGstinsDtls`)
      ) {
        toastr.error(
          'Access denied. You don’t have the required permissions for this action.',
        );
      }
      const message =
        resp.error?.message ??
        "We couldn't process your request at the moment. Please try again later.";
      if (
        !resp.url?.includes(`subscription/active`) &&
        resp.error &&
        typeof resp.error === 'string' &&
        resp.error.toLowerCase() !==
          'no such id found in database. please pass proper status id.'
      ) {
        toastr.error(message, 'Error');
      }

      if (store.isIframe()) {
        // window?.top?.postMessage('Token expired', '*');
        // store.logout();
      }

      if (resp.status === 401) {
        // store.logout();
        // router.navigate(['login']);
      }

      if (resp.status === 0 || !resp?.error?.body) {
        throw new HttpErrorResponse({ error: resp.error, status: resp.status });
      } else {
        const body = resp.error.body as BaseApiResp;
        const status = body.status.toUpperCase() === 'SUCCESS';
        const errorResp = { error: { body: { ...body, status } }, status: resp.status };
        throw new HttpErrorResponse(errorResp);
      }
    }),
  );
}

function setApiConfig(request: HttpRequest<any>, config: AppConfig, store: Store) {
  if (request.url.startsWith('http')) return request.clone();
  const options = { url: request.url, setHeaders: {} };
  if (config.env === 'prod') {
    config.tenant = store.tenant();
  }
  options.setHeaders = { ...options.setHeaders, Tenant: `${config.tenant}` };

  if (!publicApis.includes(request.url) || mgmtApis.includes(request.url)) {
    options.setHeaders = {
      ...options.setHeaders,
      'x-auth-token': `${store.authToken()}`,
      'companyid': `${store.businessContext()?.rootCompanyId}`,
    };
  }

  if (mgmtApis.includes(request.url)) {
    if (config.env !== 'local') {
      options.setHeaders = { ...options.setHeaders, product: 'SAPPHIRE' };
      options.url = `${config.sapphireMgmtApiUrl}${request.url}`;
    } else {
      options.url = `${config.sapphireApiUrl}${request.url}`;
    }
    return request.clone(options);
  }

  if (config.env !== 'local' && !publicApis.includes(request.url)) {
    options.setHeaders = { ...options.setHeaders, product: 'SAPPHIRE' };
  }

  if (config.env === 'local' || !publicApis.includes(request.url)) {
    options.url = `${config.sapphireApiUrl}${request.url}`;
  } else {
    options.url = `${config.sapphireMgmtApiUrl}${request.url}`;
  }

  return request.clone(options);
}
